import { Link } from "react-router-dom";
import { ClipboardCheck, ShieldCheck, Search, FileSearch, AlertTriangle, ArrowRight, Eye, BadgePoundSterling } from "lucide-react";
import SEO from "@/components/SEO";

const PARK_HOME = "/images/inspections-hero.jpg";

const WHY = [
  { i: <Search />, t: "Identify cause of damage", d: "Establish what has actually happened — not what a contractor wants you to believe has happened." },
  { i: <Eye />, t: "Understand whether the issue is worsening", d: "A snapshot in time tells us trajectory — repair now, monitor, or escalate." },
  { i: <ClipboardCheck />, t: "Confirm what work is actually required", d: "Scope is fact, not opinion. We document exactly what is needed and why." },
  { i: <FileSearch />, t: "Support quote review", d: "An inspection gives the technical baseline against which any quote can be measured." },
  { i: <ShieldCheck />, t: "Support insurance-related decision making", d: "Photographs, measurements and findings written in language that supports proper decisions." },
  { i: <BadgePoundSterling />, t: "Reduce risk of uninsured losses", d: "Catch issues early, before they evolve into the kind of damage insurers will not pay for." },
];

export default function Inspections() {
  return (
    <div data-testid="inspections-page">
      <SEO
        title="Park Home Inspections & Annual Health Check | Park Home Support"
        description="The Annual External Park Home Health Check is included with PHS membership. Additional independent park home inspections available at reduced member rates — identify cause, extent and correct next steps before money is wasted."
        keywords="park home health check, park home inspection, residential park home inspection UK, park home condition check, park home support"
        canonical="https://parkhomesupport.co.uk/inspections"
      />
      {/* HERO */}
      <section className="relative hero-gradient overflow-hidden">
        <div className="container-phs py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 reveal">
              <span className="eyebrow">Annual Health Check · Independent Inspections</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#071A33]">
                Your Annual Park Home Health Check — <span className="text-[#064E2E]">Plus Deeper Inspections When Needed.</span>
              </h1>
              <p className="mt-6 text-lg text-[#334155] max-w-2xl leading-relaxed">
                Every PHS membership includes an Annual External Park Home Health Check — an external visual condition review of your home with maintenance guidance. When something more detailed is needed, we provide deeper independent inspections at preferential member rates.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/membership" className="btn-primary" data-testid="ins-page-join-btn">
                  Join Now — Health Check Included <ArrowRight size={18} />
                </Link>
                <Link to="/request-support" className="btn-outline" data-testid="ins-page-request-btn">
                  Request Additional Inspection
                </Link>
              </div>
              <p className="mt-6 text-sm text-[#475569] flex items-start gap-2">
                <ShieldCheck size={16} className="text-[#064E2E] mt-0.5" />
                Members receive the Annual Health Check included plus preferential rates and priority on additional inspections.
              </p>
            </div>

            <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.15s" }}>
              <div className="img-plate">
                <img src={PARK_HOME} alt="Residential park home" className="w-full h-[460px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">Why Inspections Matter</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
              Six reasons to inspect before you spend.
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              Inspections turn assumptions into evidence — and stop expensive mistakes before they begin.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((b) => (
              <div key={b.t} className="card-default" data-testid={`ins-why-${b.t.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{b.i}</div>
                <h4 className="font-display text-xl mt-5 text-[#071A33]">{b.t}</h4>
                <p className="mt-2 text-[#334155] leading-relaxed text-[15px]">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBER BENEFIT */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="card-teal grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <span className="text-[#FFE7B6] tracking-[0.22em] uppercase text-xs font-medium">Headline Member Benefit</span>
              <h3 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">Annual External Park Home Health Check — included with every membership.</h3>
              <p className="mt-4 text-white/85 text-lg max-w-xl">
                Each year you'll receive an external visual condition review of your park home with practical maintenance guidance. Catch small issues early, before they become expensive problems. Additional in-depth inspections are available at preferential member rates.
              </p>
              <p className="mt-4 text-xs text-white/55 leading-relaxed max-w-xl">
                The Health Check is an external visual condition review with maintenance guidance — not a regulated survey, structural survey or warranty.
              </p>
            </div>
            <div className="text-center">
              <Link to="/membership" className="btn-accent w-full" data-testid="ins-member-cta">
                Join for £99 First Year <ArrowRight size={16} />
              </Link>
              <p className="text-xs text-white/70 mt-3">Then £249/year. Cancel anytime before renewal.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING NOTE + CTA */}
      <section className="section-pad bg-white">
        <div className="container-phs max-w-4xl">
          <div className="card-default" data-testid="ins-pricing-note">
            <div className="flex gap-4 items-start">
              <AlertTriangle size={22} className="text-[#B88A2A] mt-1 shrink-0" />
              <div>
                <h3 className="font-display text-2xl text-[#071A33]">Additional Inspection Fees</h3>
                <p className="mt-3 text-[#334155]">
                  Your Annual External Park Home Health Check is included with membership. Additional, deeper inspections (when needed beyond the yearly Health Check) are quoted separately and depend on location, the issue type and urgency. Members receive reduced rates and priority access.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/request-support" className="btn-primary" data-testid="ins-bottom-request-btn">
                    Request an Inspection <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="btn-outline" data-testid="ins-bottom-contact-btn">
                    Ask a Question
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
